<template>
  <div
    v-if="banner"
    :class="bannerClasses"
    class="w-full border-b text-white shadow-sm"
  >
    <div class="flex h-8 items-center justify-between gap-3 px-4 text-xs sm:px-6 lg:px-8">
      <div class="min-w-0 truncate font-medium">
        {{ banner.message }}
        <span v-if="countdownText" class="font-normal text-white/90">
          · {{ countdownText }}
        </span>
      </div>

      <a
        class="shrink-0 font-semibold underline underline-offset-2 transition-colors hover:text-white/90"
        :href="banner.linkHref"
        rel="noopener noreferrer"
        target="_blank"
      >
        {{ banner.linkLabel }}
      </a>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { PublicMaintenanceBanner, PublicStatusBanner } from '@/utils/public-status'

const props = defineProps<{
  banner: PublicStatusBanner | PublicMaintenanceBanner | null
}>()

const now = ref(Date.now())
let timer: number | undefined

onMounted(() => {
  if (props.banner?.countdownTargetAt) {
    timer = window.setInterval(() => {
      now.value = Date.now()
    }, 30_000)
  }
})

onBeforeUnmount(() => {
  if (timer) window.clearInterval(timer)
})

const bannerClasses = computed(() => {
  switch (props.banner?.tone) {
    case 'info':
      return 'border-blue-700 bg-blue-600'
    default:
      return 'border-red-700 bg-red-600'
  }
})

const countdownText = computed(() => {
  const target = props.banner?.countdownTargetAt
  if (!target) return ''
  const prefix = props.banner?.countdownPrefix || 'startet in'

  const targetDate = new Date(target)
  if (Number.isNaN(targetDate.getTime())) return ''

  const diffMs = targetDate.getTime() - now.value
  if (diffMs <= 0) return 'jetzt'

  const diffMinutes = Math.ceil(diffMs / 60_000)
  if (diffMinutes < 60) {
    return `${prefix} ${diffMinutes} Minute${diffMinutes === 1 ? '' : 'n'}`
  }

  const hours = Math.floor(diffMinutes / 60)
  const minutes = diffMinutes % 60

  if (minutes === 0) {
    return `${prefix} ${hours} Stunde${hours === 1 ? '' : 'n'}`
  }

  return `${prefix} ${hours} Stunde${hours === 1 ? '' : 'n'} ${minutes} Minute${minutes === 1 ? '' : 'n'}`
})
</script>
